/* A horizontal bar chart, and the table that sits behind it.

   `docs/design/30-design-principles.md` forbids a table as the primary view, and requires a chart
   to offer one. Both requirements are real: a chart is faster to read, and a screen reader cannot
   read a bar. So the chart is the default and the table is a toggle - not a separate screen, and
   not a modal, because a reader who needs the table needs it in place.

   The colour is the bar itself (series follows the entity). There is no swatch dot beside the
   name: it repeated the bar's colour a few pixels away, which is the decorative status dot the
   spec forbids, and the name already says which entity a bar is. */

.wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}

/* The rows share three column tracks, so every bar starts and ends at the same x whatever width
   the value beside it needs. Each row used to be its own grid, which let a long value push its
   own bar left of the one above it. */
.rows {
  display: grid;
  grid-template-columns: minmax(96px, 1.4fr) minmax(0, 3fr) max-content;
  gap: 1px 12px;
  /* The rows' own 8px of padding is the hover wash's room; pulling the grid out by the same
     amount keeps the names aligned with the panel's header text. */
  margin: 0 -8px;
}

.row {
  display: grid;
  /* Fallback where `subgrid` is unsupported: the pre-existing per-row grid. */
  grid-template-columns: minmax(96px, 1.4fr) minmax(0, 3fr) auto;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  justify-self: stretch;
  align-items: center;
  column-gap: 12px;
  /* No hairline under each row: twelve rules in a column is a ruled ledger, and the bars already
     carry the rhythm. The hover wash is the row boundary a pointer needs. */
  padding: 6px 8px;
  border: 0;
  border-radius: var(--radius-ctl);
  font-size: var(--t-dense);
  background: none;
  text-align: left;
  cursor: pointer;
  color: inherit;
}

.row:hover {
  background: var(--panel-2);
}

.row:active {
  background: var(--panel-3);
}

.rowStatic {
  cursor: default;
}

.rowStatic:hover {
  background: none;
}

.name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--ink-2);
}

.track {
  height: 6px;
  border-radius: var(--radius-pill);
  background: var(--panel-3);
  overflow: hidden;
}

.fill {
  display: block;
  height: 100%;
  border-radius: var(--radius-pill);
  min-width: 2px;
}

.value {
  text-align: right;
  font-variant-numeric: lining-nums tabular-nums;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
}

.meta {
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 400;
  margin-left: 6px;
}

.toggle {
  align-self: flex-start;
  font-size: var(--t-min);
  font-weight: 500;
  border: 1px solid var(--line-2);
  background: var(--panel);
  color: var(--ink-2);
  border-radius: var(--radius-ctl);
  padding: 3px 9px;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle:hover {
  background: var(--panel-2);
  color: var(--ink);
}

.toggle:active {
  background: var(--panel-3);
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--t-dense);
}

.table caption {
  text-align: left;
  color: var(--muted);
  font-size: var(--t-min);
  padding-bottom: 6px;
}

.table thead th {
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 500;
}

.table th,
.table td {
  text-align: left;
  padding: 6px 8px 6px 0;
  border-bottom: 1px solid var(--line);
}

.table tbody th {
  font-weight: 400;
  color: var(--ink-2);
}

.table td:last-child,
.table th:last-child {
  text-align: right;
  padding-right: 0;
  font-variant-numeric: lining-nums tabular-nums;
}

.table td:last-child {
  font-weight: 600;
  color: var(--ink);
}
